import { Link, useLocation } from 'react-router-dom'
import {
    Home, Car, Map, Newspaper, PlayCircle, User, LogIn,
    ChevronLeft, ChevronRight
} from 'lucide-react'
import { useAuthStore, useLanguageStore } from '../../lib/store'

interface SidebarProps {
    isOpen: boolean
    onToggle: () => void
}

export default function Sidebar({ isOpen, onToggle }: SidebarProps) {
    const location = useLocation()
    const { user, isAuthenticated } = useAuthStore()
    const { language } = useLanguageStore()

    const menuItems = [
        { path: '/', icon: Home, label: { en: 'Home', vi: 'Trang chủ' } },
        { path: '/vehicles', icon: Car, label: { en: 'Vehicles', vi: 'Xe' } },
        { path: '/tours', icon: Map, label: { en: 'Tours', vi: 'Tour' } },
        { path: '/blog', icon: Newspaper, label: { en: 'Blog', vi: 'Blog' } },
        { path: '/shorts', icon: PlayCircle, label: { en: 'Shorts', vi: 'Video ngắn' } },
    ]

    const isActive = (path: string) => {
        if (path === '/') return location.pathname === '/'
        return location.pathname.startsWith(path)
    }

    return (
        <>
            {/* Mobile Overlay */}
            {isOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                    onClick={onToggle}
                />
            )}

            {/* Sidebar */}
            <aside
                className={`fixed left-0 top-0 h-full bg-gray-900 text-white z-50 transition-all duration-300 ease-in-out ${isOpen ? 'w-64' : 'w-0 lg:w-16'
                    } overflow-hidden`}
            >
                {/* Logo */}
                <div className="h-16 flex items-center justify-between px-4 border-b border-gray-800">
                    {isOpen && (
                        <Link to="/" className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-orange-500 rounded-lg flex items-center justify-center font-bold">
                                P
                            </div>
                            <span className="font-bold text-lg">Phú Quốc</span>
                        </Link>
                    )}
                    <button
                        onClick={onToggle}
                        className="p-2 hover:bg-gray-800 rounded-lg transition hidden lg:block"
                    >
                        {isOpen ? <ChevronLeft className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                    </button>
                </div>

                {/* Menu Items */}
                <nav className="py-4">
                    {menuItems.map((item) => {
                        const Icon = item.icon
                        const active = isActive(item.path)

                        return (
                            <Link
                                key={item.path}
                                to={item.path}
                                className={`flex items-center gap-3 px-4 py-3 mx-2 rounded-lg transition ${active
                                    ? 'bg-primary-600 text-white'
                                    : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                                    }`}
                            >
                                <Icon className="w-5 h-5 flex-shrink-0" />
                                {isOpen && <span>{item.label[language]}</span>}
                            </Link>
                        )
                    })}
                </nav>

                {/* User Section */}
                <div className="absolute bottom-0 left-0 right-0 border-t border-gray-800 p-4">
                    {isAuthenticated && user ? (
                        <Link
                            to={user.role === 'admin' ? '/admin' : user.role === 'owner' ? '/owner' : '/dashboard'}
                            className="flex items-center gap-3 text-gray-400 hover:text-white transition"
                        >
                            <div className="w-8 h-8 bg-gray-700 rounded-full flex items-center justify-center">
                                <User className="w-4 h-4" />
                            </div>
                            {isOpen && (
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-white truncate">{user.fullName}</p>
                                    <p className="text-xs text-gray-500">{user.role}</p>
                                </div>
                            )}
                        </Link>
                    ) : (
                        <Link
                            to="/login"
                            className="flex items-center gap-3 px-4 py-2 bg-primary-600 hover:bg-primary-700 rounded-lg transition text-white"
                        >
                            <LogIn className="w-5 h-5" />
                            {isOpen && <span>{language === 'en' ? 'Login' : 'Đăng nhập'}</span>}
                        </Link>
                    )}
                </div>
            </aside>
        </>
    )
}
